'use client';

import type { ComponentType, ReactElement, ReactNode } from 'react';
import { UserMenuInitialTab } from '@/contexts/layout';
import { ModalLocation } from '@/constants/componentPresets';
import { SessionsDisplayMode } from '@/types/frontend-types';
import type { TeamDetail } from '@cabal/shared/domain/session/team/types.js';
import type { CsessionsProps } from '../Csessions';
import type { CusermenuProps } from '../Cusermenu';
import { CenterModal } from './CenterModal';
import { SessionsModal } from './SessionsModal';
import { UserMenuModal } from './UserMenuModal';

interface CpagelayoutModalsProps {
  SessionsComponent: ComponentType<CsessionsProps>;
  UserMenuComponent: ComponentType<CusermenuProps>;
  currentSessionId?: string;
  displayMode: SessionsDisplayMode;
  externalModalContent?: ReactNode;
  externalModalLocation?: ModalLocation;
  externalModalUseContainerBackground: boolean;
  externalModalVisible?: boolean;
  isSearchInitiated: boolean;
  isSessionsModalOpen: boolean;
  isUserMenuModalOpen: boolean;
  onCenterModalClose: () => void;
  onDisplayModeToggle: () => void;
  onReplicateTeam: (teamConfig: TeamDetail) => void;
  onSearchQueryChange: (query: string) => void;
  onSearchToggle: () => void;
  onSessionCountChange: (count: number | null) => void;
  onSessionsModalClose: () => void;
  onStartNewSession?: () => void;
  onUserMenuModalClose: () => void;
  requestUserMenuClose: () => boolean | void;
  onUserMenuUnsavedChangesChange: (hasUnsavedChanges: boolean) => void;
  searchQuery: string;
  sessionCount: number | null;
  userMenuInitialTab: UserMenuInitialTab;
}

export function CpagelayoutModals({
  SessionsComponent,
  UserMenuComponent,
  currentSessionId,
  displayMode,
  externalModalContent,
  externalModalLocation,
  externalModalUseContainerBackground,
  externalModalVisible,
  isSearchInitiated,
  isSessionsModalOpen,
  isUserMenuModalOpen,
  onCenterModalClose,
  onDisplayModeToggle,
  onReplicateTeam,
  onSearchQueryChange,
  onSearchToggle,
  onSessionCountChange,
  onSessionsModalClose,
  onStartNewSession,
  onUserMenuModalClose,
  requestUserMenuClose,
  onUserMenuUnsavedChangesChange,
  searchQuery,
  sessionCount,
  userMenuInitialTab,
}: CpagelayoutModalsProps): ReactElement {
  return (
    <>
      <SessionsModal
        SessionsComponent={SessionsComponent}
        currentSessionId={currentSessionId}
        displayMode={displayMode}
        isSearchInitiated={isSearchInitiated}
        isSessionsModalOpen={isSessionsModalOpen}
        onDisplayModeToggle={onDisplayModeToggle}
        onReplicateTeam={onReplicateTeam}
        onSearchQueryChange={onSearchQueryChange}
        onSearchToggle={onSearchToggle}
        onSessionCountChange={onSessionCountChange}
        onSessionsModalClose={onSessionsModalClose}
        onStartNewSession={onStartNewSession}
        searchQuery={searchQuery}
        sessionCount={sessionCount}
      />

      <UserMenuModal
        UserMenuComponent={UserMenuComponent}
        isUserMenuModalOpen={isUserMenuModalOpen}
        onUserMenuModalClose={onUserMenuModalClose}
        requestUserMenuClose={requestUserMenuClose}
        onUserMenuUnsavedChangesChange={onUserMenuUnsavedChangesChange}
        userMenuInitialTab={userMenuInitialTab}
      />

      <CenterModal
        visible={externalModalVisible}
        location={externalModalLocation ?? ModalLocation.Center}
        content={externalModalContent}
        useContainerBackground={externalModalUseContainerBackground}
        onClose={onCenterModalClose}
      />
    </>
  );
}
